import {
  Box,
  Container,
  Paper,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Toolbar,
  SelectChangeEvent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  ViewModule as ViewModuleIcon,
  Person as PersonIcon,
  MenuBook as MenuBookIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  AutoFixHigh as AutoFixIcon,
  DeleteSweep as DeleteSweepIcon,
  Close as CloseIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { addWeeks, startOfWeek, format } from 'date-fns';
import { useTimetableDesignerStore } from '../store/timetableDesignerStore';
import { useAuthStore } from '../store/authStore';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getClassSectionsDirect,
  getTeachersDirect,
  getSubjectsDirect,
  getAcademicYearsDirect,
  getSchedulePeriodsByScheduleId,
  getTimetableRecordsDirect,
  deleteAllForClassSectionDirect,
} from '@timetable/api';
import { useRealtimeTimetable } from '@timetable/hooks';
import { queryKeys } from '../lib/queryKeys';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { EmptyState } from '../components/EmptyState';
import { useToast } from '../components/Toast';
import {
  TimetableGrid,
  MultiSectionTimetableGrid,
  BulkOperationsToolbar,
  TimetableHistoryViewer,
  TemporalDatePicker,
  ModeToggle,
  PeriodEditingDialog,
  ConflictVisualizationPanel,
  RulesConfigurationDialog,
  SupervisionAssignmentDialog,
  AutoFillProgressDialog,
} from '../components/timetable';
import type { Conflict, SchoolRules, AutoFillLookups } from '../components/timetable';
import type { AutoFillResponse } from '@timetable/types';
import { HelpProvider } from '../components/timetable/ContextualHelpSystem';
import { useUIStore } from '../store/uiStore';
import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMultiSectionTimetable } from '../hooks/useMultiSectionTimetable';
import { useAutoFill } from '../hooks/useMultiSectionTimetable';
import type { TimetableRecordWithRelations, DayOfWeek, SchedulePeriod, AcademicYear } from '@timetable/types';
import { detectAllConflicts } from '../utils/conflictDetection';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Duration of the discard undo countdown in ms */
const DISCARD_UNDO_MS = 3000;
/** How often the countdown ticks in ms */
const DISCARD_TICK_MS = 50;

export function TimetableDesignerPage() {
  const { schoolContext, role } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToast();
  const setTimetableStatus = useUIStore((state) => state.setTimetableStatus);
  const clearTimetableStatus = useUIStore((state) => state.clearTimetableStatus);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  // Period editing dialog state
  const [periodEditingDialogOpen, setPeriodEditingDialogOpen] = useState(false);
  const [selectedPeriodSlot, setSelectedPeriodSlot] = useState<{
    sectionId: string;
    periodId: string;
    day: DayOfWeek;
  } | null>(null);
  const [recordForEditing, setRecordForEditing] = useState<TimetableRecordWithRelations | null>(null);

  // Conflict panel state
  const [conflictPanelOpen, setConflictPanelOpen] = useState(false);

  // Rules dialog state
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);

  // Supervision dialog state
  const [supervisionDialogOpen, setSupervisionDialogOpen] = useState(false);
  const [supervisionPeriod, setSupervisionPeriod] = useState<SchedulePeriod | null>(null);
  const [supervisionSectionId, setSupervisionSectionId] = useState<string>('');
  const [supervisionDay, setSupervisionDay] = useState<DayOfWeek>('MONDAY');

  // Auto-fill
  const autoFillMutation = useAutoFill();

  // ── Auto-Fill progress dialog state ───────────────────────────────────────
  const [autoFillDialogOpen, setAutoFillDialogOpen] = useState(false);
  const [autoFillResult, setAutoFillResult] = useState<AutoFillResponse | null>(null);
  const [autoFillError, setAutoFillError] = useState<string | null>(null);

  // ── Discard All state ──────────────────────────────────────────────────────
  const [discardWarningOpen, setDiscardWarningOpen] = useState(false);
  const [discardCountdown, setDiscardCountdown] = useState<number | null>(null);
  const [discardRunning, setDiscardRunning] = useState(false);
  const discardTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const discardCancelledRef = useRef(false);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (discardTimerRef.current) clearInterval(discardTimerRef.current);
    };
  }, []);

  useEffect(() => {
    return () => { clearTimetableStatus(); };
  }, [clearTimetableStatus]);

  const {
    viewMode,
    selectedEntity,
    selectedWeek,
    temporalDate,
    editMode,
    activeAcademicYear,
    setViewMode,
    selectEntity,
    setWeek,
    setTemporalDate,
    setActiveAcademicYear,
    setEditMode,
  } = useTimetableDesignerStore();

  const handleRealtimeChange = useCallback(
    (payload: any) => {
      const eventType = payload.eventType;
      let message = '';
      switch (eventType) {
        case 'INSERT': message = 'Timetable updated: A new period was added'; break;
        case 'UPDATE': message = 'Timetable updated: A period was modified'; break;
        case 'DELETE': message = 'Timetable updated: A period was removed'; break;
        default: message = 'Timetable was updated';
      }
      setTimetableStatus(message, new Date());
    },
    [setTimetableStatus]
  );

  useRealtimeTimetable(schoolContext || '', {
    enabled: !!schoolContext,
    onChangeDetected: handleRealtimeChange,
  });

  // Fetch all academic years for the year selector
  const { data: allAcademicYears, isLoading: loadingAcademicYears } = useQuery({
    queryKey: queryKeys.academicYears.list({ school_id: schoolContext || '' }),
    queryFn: () => getAcademicYearsDirect({ school_id: schoolContext! }),
    enabled: !!schoolContext,
  });

  // Derive the selected academic year from store (defaults to published year)
  const selectedAcademicYear: AcademicYear | null = activeAcademicYear ?? null;

  // On first load, set the published year as active
  useEffect(() => {
    if (!activeAcademicYear && allAcademicYears && allAcademicYears.length > 0) {
      const published = allAcademicYears.find(y => y.status === 'published');
      if (published) setActiveAcademicYear(published);
    }
  }, [allAcademicYears, activeAcademicYear, setActiveAcademicYear]);

  // Force read-only when archived year is selected (task 11.10)
  useEffect(() => {
    if (selectedAcademicYear?.status === 'archived' && editMode) {
      setEditMode(false);
    }
  }, [selectedAcademicYear, editMode, setEditMode]);

  const academicYear = selectedAcademicYear?.name ?? null;
  const academicYearId = selectedAcademicYear?.id ?? null;
  const isArchivedYear = selectedAcademicYear?.status === 'archived';

  const { data: classSections, isLoading: loadingClasses } = useQuery({
    queryKey: queryKeys.classSections.list({ school_id: schoolContext || '' }),
    queryFn: () => getClassSectionsDirect(schoolContext || ''),
    enabled: !!schoolContext && viewMode === 'class',
  });

  const { data: teachers, isLoading: loadingTeachers } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolContext || '' }),
    queryFn: () => getTeachersDirect({ school_id: schoolContext || '' }),
    enabled: !!schoolContext && viewMode === 'teacher',
  });

  const { data: subjects, isLoading: loadingSubjects } = useQuery({
    queryKey: queryKeys.subjects.list({ school_id: schoolContext || '' }),
    queryFn: () => getSubjectsDirect({ school_id: schoolContext || '' }),
    enabled: !!schoolContext && viewMode === 'subject',
  });

  const { data: multiSectionData, isLoading: loadingMultiSection } = useMultiSectionTimetable(
    viewMode === 'class' ? selectedEntity : null,
    academicYear,
    temporalDate?.toISOString().split('T')[0],
    { enabled: viewMode === 'class' && !!selectedEntity && !!academicYear, academicYearId }
  );

  // Fetch ALL school records for cross-class conflict detection (teacher double-booking across classes)
  const { data: allSchoolRecords } = useQuery({
    queryKey: queryKeys.timetable.list({ school_id: schoolContext || '', academic_year: academicYear || '' }),
    queryFn: () => getTimetableRecordsDirect({ school_id: schoolContext!, academic_year: academicYear! }),
    enabled: !!schoolContext && !!academicYear,
    staleTime: 30_000,
  });

  // Derive selected class data and its class_schedule_id
  const selectedClassData = classSections?.find(
    cs => cs.class_id === selectedEntity || cs.class?.id === selectedEntity
  )?.class;

  const scheduleId = (selectedClassData as any)?.class_schedule_id ?? null;

  // Fetch schedule periods for the selected class's schedule
  const { data: schedulePeriods } = useQuery({
    queryKey: queryKeys.schedulePeriods.bySchedule(scheduleId!),
    queryFn: () => getSchedulePeriodsByScheduleId(scheduleId!),
    enabled: !!scheduleId,
  });

  const DAY_ORDER: DayOfWeek[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

  // Derive working days from the selected class's schedule working_days
  const workingDays: DayOfWeek[] = useMemo(() => {
    const scheduleWorkingDays = (selectedClassData as any)?.class_schedule?.working_days
      ?? (selectedClassData as any)?.working_days;
    if (scheduleWorkingDays?.length) {
      return DAY_ORDER.filter(d => (scheduleWorkingDays as string[]).includes(d));
    }
    return ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];
  }, [selectedClassData]);

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: 'class' | 'teacher' | 'subject' | null
  ) => {
    if (newMode) {
      setViewMode(newMode);
      selectEntity(null);
    }
  };

  const handleEntityChange = (event: SelectChangeEvent<string>) => {
    selectEntity(event.target.value);
  };

  const handleYearChange = (event: SelectChangeEvent<string>) => {
    const year = allAcademicYears?.find(y => y.id === event.target.value);
    if (year) setActiveAcademicYear(year);
  };

  const handlePreviousWeek = () => setWeek(addWeeks(selectedWeek, -1));
  const handleNextWeek = () => setWeek(addWeeks(selectedWeek, 1));

  const handlePeriodClick = (sectionId: string, periodId: string, day: DayOfWeek) => {
    const period = schedulePeriods?.find(p => p.id === periodId);
    if (period?.is_break) {
      setSupervisionPeriod(period);
      setSupervisionSectionId(sectionId);
      setSupervisionDay(day);
      setSupervisionDialogOpen(true);
      return;
    }
    setSelectedPeriodSlot({ sectionId, periodId, day });
    setRecordForEditing(null);
    setPeriodEditingDialogOpen(true);
  };

  const handlePeriodEdit = (record: TimetableRecordWithRelations) => {
    setSelectedPeriodSlot({
      sectionId: record.class_section_id,
      periodId: record.period_id,
      day: (record.day_of_week as string).toUpperCase() as DayOfWeek,
    });
    setRecordForEditing(record);
    setPeriodEditingDialogOpen(true);
  };

  const handleClosePeriodEditingDialog = () => {
    setPeriodEditingDialogOpen(false);
    setSelectedPeriodSlot(null);
    setRecordForEditing(null);
  };

  const handleConflictClick = (_conflict: Conflict) => {
    setConflictPanelOpen(false);
  };

  const handleSaveRules = (_rules: SchoolRules) => {
    setRulesDialogOpen(false);
  };

  // ── Discard All handlers ───────────────────────────────────────────────────

  const handleDiscardAllClick = () => {
    setDiscardWarningOpen(true);
  };

  const handleDiscardConfirm = () => {
    setDiscardWarningOpen(false);
    discardCancelledRef.current = false;
    setDiscardCountdown(DISCARD_UNDO_MS);

    discardTimerRef.current = setInterval(() => {
      setDiscardCountdown((prev) => {
        if (prev === null) return null;
        const next = prev - DISCARD_TICK_MS;
        if (next <= 0) {
          clearInterval(discardTimerRef.current!);
          discardTimerRef.current = null;
          if (!discardCancelledRef.current) {
            executeDiscardAll();
          }
          return null;
        }
        return next;
      });
    }, DISCARD_TICK_MS);
  };

  const handleDiscardCancel = () => {
    discardCancelledRef.current = true;
    if (discardTimerRef.current) {
      clearInterval(discardTimerRef.current);
      discardTimerRef.current = null;
    }
    setDiscardCountdown(null);
  };

  const executeDiscardAll = async () => {
    if (!schoolContext || sections.length === 0) return;
    setDiscardRunning(true);
    try {
      await Promise.all(sections.map((s) => deleteAllForClassSectionDirect(s.id)));
      queryClient.invalidateQueries({ queryKey: queryKeys.timetable.all });
      showSuccess(`Discarded all timetable records for ${sections.length} section${sections.length !== 1 ? 's' : ''}`);
    } catch (err: any) {
      showError(err.message || 'Failed to discard timetable records');
    } finally {
      setDiscardRunning(false);
    }
  };

  const handleAutoFill = async () => {
    if (!selectedEntity || !academicYear || !academicYearId) return;
    // Reset state and open dialog before the API call so the loading phase is visible
    setAutoFillResult(null);
    setAutoFillError(null);
    setAutoFillDialogOpen(true);
    try {
      const result = await autoFillMutation.mutateAsync({
        classId: selectedEntity,
        sectionIds: sections.map(s => s.id),
        academicYear,
        academicYearId,
        date: temporalDate ? temporalDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      });
      setAutoFillResult(result);
    } catch (err: any) {
      setAutoFillError(err.message || 'Auto-fill failed');
    }
  };

  const handleAutoFillDialogClose = () => {
    setAutoFillDialogOpen(false);
    setAutoFillResult(null);
    setAutoFillError(null);
  };

  const getEntityOptions = () => {
    if (viewMode === 'class') {
      const seen = new Set<string>();
      return (classSections || []).filter(s => {
        if (seen.has(s.class_id)) return false;
        seen.add(s.class_id);
        return true;
      });
    } else if (viewMode === 'teacher') {
      return teachers || [];
    } else {
      return subjects || [];
    }
  };

  const getEntityLabel = (entity: any) => {
    if (viewMode === 'class') return entity.class.class_name;
    else if (viewMode === 'teacher') return entity.name;
    else return entity.subject_name;
  };

  const getEntityId = (entity: any) => {
    if (viewMode === 'class') return entity.class_id;
    return entity.id;
  };

  const isLoading = loadingAcademicYears || loadingClasses || loadingTeachers || loadingSubjects || (viewMode === 'class' && loadingMultiSection);
  const entities = getEntityOptions();

  const weekStart = startOfWeek(selectedWeek, { weekStartsOn: 1 });
  const weekEnd = addWeeks(weekStart, 1);

  const containerProps = editMode
    ? {
        maxWidth: false as const,
        sx: {
          py: 1,
          px: 2,
          height: 'calc(100vh - 64px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column' as const,
        },
      }
    : {
        maxWidth: 'xl' as const,
        sx: { py: 3 },
      };

  const sections = multiSectionData?.sections ?? [];
  const records = multiSectionData?.records ?? [];
  // Use all school records for conflict detection so cross-class teacher conflicts are visible
  const conflicts = useMemo(() => detectAllConflicts(allSchoolRecords ?? records), [allSchoolRecords, records]);

  // ── Auto-Fill lookup maps — resolve IDs → display names for the progress dialog ──
  const autoFillLookups = useMemo((): AutoFillLookups => {
    const periodsById = new Map<string, { period_number: number; time_start: string; time_end: string }>();
    for (const p of schedulePeriods ?? []) {
      periodsById.set(p.id, { period_number: p.period_number, time_start: p.time_start, time_end: p.time_end });
    }

    // Subjects: collect from existing records (they carry the joined subject object)
    const subjectsById = new Map<string, string>();
    for (const r of records) {
      if (r.subject_id && (r as any).subject?.subject_name) {
        subjectsById.set(r.subject_id, (r as any).subject.subject_name);
      }
    }

    // Sections: id → "ClassName SectionLetter"
    const sectionsById = new Map<string, string>();
    for (const s of sections) {
      const className = (s as any).class?.class_name ?? '';
      const sectionLetter = (s as any).section ?? '';
      sectionsById.set(s.id, `${className}${sectionLetter ? ' ' + sectionLetter : ''}`);
    }

    // Teachers: collect from existing records AND allSchoolRecords (covers newly assigned teachers)
    const teachersById = new Map<string, string>();
    for (const r of [...records, ...(allSchoolRecords ?? [])]) {
      if (r.teacher_id && (r as any).teacher?.name) {
        teachersById.set(r.teacher_id, (r as any).teacher.name);
      }
    }
    // Also pull from the teachers list query if it happens to be loaded
    for (const t of teachers ?? []) {
      if (t.id && t.name) teachersById.set(t.id, t.name);
    }

    return { periodsById, subjectsById, sectionsById, teachersById };
  }, [schedulePeriods, records, sections, allSchoolRecords, teachers]);

  // Determine which years are visible in the selector (admins see all, others only published/archived)
  const isAdmin = role === 'admin' || role === 'super_admin';
  const visibleYears = (allAcademicYears ?? []).filter(y =>
    y.status === 'published' || y.status === 'archived' || (isAdmin && y.status === 'draft')
  );

  // Discard All derived state
  const isDiscardCounting = discardCountdown !== null;
  const discardProgress = isDiscardCounting
    ? ((DISCARD_UNDO_MS - discardCountdown!) / DISCARD_UNDO_MS) * 100
    : 0;
  const canDiscardAll = isAdmin && editMode && !isArchivedYear && records.length > 0 && !!selectedEntity && viewMode === 'class';

  return (
    <HelpProvider>
      <Container {...containerProps}>
        <Box sx={{ mb: editMode ? 1 : 3 }}>
          <Typography variant="h4" gutterBottom>
            Timetable Designer
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage timetables for all class sections
          </Typography>
        </Box>

        {/* Filter Bar */}
        <Paper sx={{ p: editMode ? 1.5 : 2, mb: editMode ? 1 : 3 }}>
          <Toolbar disableGutters sx={{ gap: 2, flexWrap: 'wrap' }}>
            {/* View Mode Selector */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="view mode"
              size="small"
            >
              <ToggleButton value="class" aria-label="class view">
                <ViewModuleIcon sx={{ mr: 1 }} />
                Class
              </ToggleButton>
              <ToggleButton value="teacher" aria-label="teacher view">
                <PersonIcon sx={{ mr: 1 }} />
                Teacher
              </ToggleButton>
              <ToggleButton value="subject" aria-label="subject view">
                <MenuBookIcon sx={{ mr: 1 }} />
                Subject
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Academic Year Selector */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="year-select-label">Academic Year</InputLabel>
              <Select
                labelId="year-select-label"
                id="year-select"
                value={selectedAcademicYear?.id || ''}
                label="Academic Year"
                onChange={handleYearChange}
                disabled={loadingAcademicYears}
              >
                {visibleYears.map(year => (
                  <MenuItem key={year.id} value={year.id}>
                    {year.name}
                    {year.status !== 'published' && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({year.status})
                      </Typography>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Entity Selector */}
            <FormControl size="small" sx={{ minWidth: 250 }}>
              <InputLabel id="entity-select-label">
                {viewMode === 'class' ? 'Select Class' : viewMode === 'teacher' ? 'Select Teacher' : 'Select Subject'}
              </InputLabel>
              <Select
                labelId="entity-select-label"
                id="entity-select"
                value={selectedEntity || ''}
                label={viewMode === 'class' ? 'Select Class' : viewMode === 'teacher' ? 'Select Teacher' : 'Select Subject'}
                onChange={handleEntityChange}
                disabled={isLoading}
              >
                {entities.map((entity: any) => (
                  <MenuItem key={getEntityId(entity)} value={getEntityId(entity)}>
                    {getEntityLabel(entity)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Mode Toggle — hidden for archived years */}
            {!isArchivedYear && <ModeToggle disabled={!selectedEntity} />}

            {/* Week Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              <IconButton onClick={handlePreviousWeek} size="small">
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 200, textAlign: 'center' }}>
                Week of {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
              </Typography>
              <IconButton onClick={handleNextWeek} size="small">
                <ChevronRightIcon />
              </IconButton>
            </Box>

            {/* Bulk Operations */}
            <BulkOperationsToolbar
              viewMode={viewMode}
              selectedEntity={selectedEntity}
              selectedWeek={selectedWeek}
            />

            {/* Rules Configuration Button — hidden for archived years */}
            {viewMode === 'class' && selectedEntity && editMode && !isArchivedYear && (
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => setRulesDialogOpen(true)}
                size="small"
              >
                Rules
              </Button>
            )}

            {/* Auto-Fill Button — hidden for archived years */}
            {viewMode === 'class' && selectedEntity && editMode && !isArchivedYear && (
              <Button
                variant="outlined"
                startIcon={autoFillMutation.isPending ? <CircularProgress size={14} /> : <AutoFixIcon />}
                onClick={handleAutoFill}
                disabled={autoFillMutation.isPending || sections.length === 0}
                size="small"
                color="secondary"
              >
                {autoFillMutation.isPending ? 'Filling…' : 'Auto-Fill'}
              </Button>
            )}

            {/* Discard All Button — only in edit mode, class view, non-archived */}
            {canDiscardAll && (
              isDiscardCounting ? (
                <Tooltip title="Tap to cancel discard">
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleDiscardCancel}
                    sx={{
                      minWidth: 160,
                      position: 'relative',
                      overflow: 'hidden',
                      borderColor: 'error.main',
                      color: 'error.main',
                      '&:hover': { bgcolor: 'error.light' },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${discardProgress}%`,
                        bgcolor: 'error.light',
                        transition: `width ${DISCARD_TICK_MS}ms linear`,
                        pointerEvents: 'none',
                      }}
                    />
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                      <span>Tap to cancel ({Math.ceil(discardCountdown! / 1000)}s)</span>
                    </Box>
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip title="Discard all timetable records for this class">
                  <span>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={discardRunning ? <CircularProgress size={14} color="inherit" /> : <DeleteSweepIcon />}
                      disabled={discardRunning || isDiscardCounting}
                      onClick={handleDiscardAllClick}
                    >
                      {discardRunning ? 'Discarding…' : 'Discard all'}
                    </Button>
                  </span>
                </Tooltip>
              )
            )}

            {/* Conflict Panel Button — hidden for archived years */}
            {viewMode === 'class' && selectedEntity && !isArchivedYear && (
              <Button
                variant="outlined"
                startIcon={
                  <Badge badgeContent={conflicts.length} color="error">
                    <WarningIcon />
                  </Badge>
                }
                onClick={() => setConflictPanelOpen(true)}
                size="small"
                color={conflicts.length > 0 ? 'error' : 'inherit'}
              >
                Conflicts
              </Button>
            )}

            {/* History Viewer Button */}
            {viewMode === 'class' && selectedEntity && (
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => setHistoryDialogOpen(true)}
                size="small"
              >
                History
              </Button>
            )}
          </Toolbar>
        </Paper>

        {/* Archived year banner */}
        {isArchivedYear && (
          <Alert severity="info" sx={{ mb: editMode ? 1 : 3 }}>
            You are viewing an archived academic year. This timetable is read-only.
          </Alert>
        )}

        {/* Temporal Date Picker */}
        {viewMode === 'class' && selectedEntity && (
          <Box sx={{ mb: editMode ? 1 : 3 }}>
            <TemporalDatePicker
              selectedDate={temporalDate}
              onDateChange={setTemporalDate}
            />
          </Box>
        )}

        {/* Main Content */}
        {isLoading ? (
          <LoadingSpinner />
        ) : !academicYear ? (
          <EmptyState
            title="No Published Academic Year"
            description="Please set up and publish an academic year in Settings before using the Timetable Designer."
            action={
              <Button variant="contained" onClick={() => navigate('/app/settings')}>
                Go to Settings
              </Button>
            }
          />
        ) : !selectedEntity ? (
          <EmptyState
            title="No Selection"
            description={`Please select a ${viewMode} to view the timetable`}
          />
        ) : viewMode === 'class' && selectedEntity && !scheduleId ? (
          <EmptyState
            title="No Schedule Assigned"
            description="This class has no class schedule assigned. Please assign a schedule in Master Data."
            action={
              <Button variant="contained" onClick={() => navigate('/app/master-data')}>
                Go to Master Data
              </Button>
            }
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flex: editMode ? 1 : undefined,
              minHeight: 0,
              gap: 2,
              overflow: 'hidden',
            }}
          >
            {/* Grid area */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                minWidth: 0,
                pb: 2,
              }}
            >
              {viewMode === 'class' ? (
                multiSectionData ? (
                  <MultiSectionTimetableGrid
                    classId={selectedEntity}
                    editMode={editMode && !isArchivedYear}
                    temporalDate={temporalDate}
                    sections={sections}
                    schedulePeriods={schedulePeriods || []}
                    workingDays={workingDays}
                    records={records}
                    conflicts={conflicts}
                    onPeriodClick={handlePeriodClick}
                    onPeriodEdit={handlePeriodEdit}
                  />
                ) : (
                  <LoadingSpinner />
                )
              ) : (
                <TimetableGrid
                  entityId={selectedEntity}
                  viewMode={viewMode}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Period Editing Dialog — not shown for archived years */}
        {selectedPeriodSlot && !isArchivedYear && (
          <PeriodEditingDialog
            open={periodEditingDialogOpen}
            onClose={handleClosePeriodEditingDialog}
            sectionId={selectedPeriodSlot.sectionId}
            periodId={selectedPeriodSlot.periodId}
            day={selectedPeriodSlot.day}
            existingRecord={recordForEditing || undefined}
            allRecords={records}
            schedulePeriods={schedulePeriods}
            sections={sections}
            onSave={() => { handleClosePeriodEditingDialog(); }}
            onDelete={() => { handleClosePeriodEditingDialog(); }}
            academicYear={academicYear ?? undefined}
            academicYearId={academicYearId ?? undefined}
          />
        )}

        {/* Supervision Assignment Dialog — not shown for archived years */}
        {supervisionPeriod && !isArchivedYear && (
          <SupervisionAssignmentDialog
            open={supervisionDialogOpen}
            onClose={() => setSupervisionDialogOpen(false)}
            sectionId={supervisionSectionId}
            period={supervisionPeriod}
            day={supervisionDay}
            existingRecords={records}
            onSave={() => setSupervisionDialogOpen(false)}
            academicYear={academicYear ?? undefined}
            academicYearId={academicYearId ?? undefined}
          />
        )}

        {/* Conflict Visualization Panel */}
        <ConflictVisualizationPanel
          conflicts={conflicts}
          open={conflictPanelOpen}
          onClose={() => setConflictPanelOpen(false)}
          onConflictClick={handleConflictClick}
        />

        {/* Rules Configuration Dialog */}
        {schoolContext && (
          <RulesConfigurationDialog
            open={rulesDialogOpen}
            onClose={() => setRulesDialogOpen(false)}
            schoolId={schoolContext}
            onSave={handleSaveRules}
          />
        )}

        {/* History Viewer Dialog */}
        <Dialog
          open={historyDialogOpen}
          onClose={() => setHistoryDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Timetable Change History</DialogTitle>
          <DialogContent sx={{ minHeight: 500 }}>
            {selectedEntity && viewMode === 'class' && (
              <TimetableHistoryViewer
                classSectionId={selectedEntity}
                academicYear={academicYear ?? ''}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Auto-Fill Progress Dialog */}
        <AutoFillProgressDialog
          open={autoFillDialogOpen}
          isLoading={autoFillMutation.isPending}
          result={autoFillResult}
          errorMessage={autoFillError}
          lookups={autoFillLookups}
          onClose={handleAutoFillDialogClose}
        />

        {/* Discard All Warning Dialog */}
        <Dialog
          open={discardWarningOpen}
          onClose={() => setDiscardWarningOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteSweepIcon color="error" />
            Discard all records?
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                mb: 2,
                bgcolor: '#FEF2F2',
                borderRadius: 1,
                border: '1px solid #FECACA',
              }}
            >
              <WarningAmberIcon sx={{ color: '#991B1B', fontSize: 18, flexShrink: 0 }} />
              <Typography variant="body2" sx={{ color: '#991B1B', fontWeight: 500 }}>
                This cannot be undone.
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              All timetable records for the selected class ({sections.length} section{sections.length !== 1 ? 's' : ''}) will be permanently deleted.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You will have <strong>3 seconds</strong> to cancel after confirming.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button variant="outlined" onClick={() => setDiscardWarningOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={handleDiscardConfirm} startIcon={<DeleteSweepIcon />}>
              Yes, discard all
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </HelpProvider>
  );
}
